import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip } from "@mui/material";
import PageHeader from "../../components/common/PageHeader";
import DataTable from "../../components/common/DataTable";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import AlertMessage from "../../components/common/AlertMessage";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import { useAlert } from "../../context/AlertContext";
import api from "../../services/api";
import { Provider, TableColumn } from "../../types";
import { formatRelativeTime } from "../../utils/formatters";

const ProviderList: React.FC = () => {
  const navigate = useNavigate();
  const alert = useAlert();

  // State variables
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [providerToDelete, setProviderToDelete] = useState<Provider | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Fetch providers
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoading(true);
        const response = await api.providers.getAll();
        setProviders(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching providers:", error);
        setError("Failed to load providers. Please try again.");
        alert.error("Failed to fetch providers");
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [alert]);

  // Get provider chip color
  const getProviderColor = (
    provider: string,
  ):
    | "primary"
    | "secondary"
    | "success"
    | "info"
    | "warning"
    | "error"
    | "default" => {
    if (provider.startsWith("openai")) return "primary";
    if (provider.startsWith("anthropic")) return "info";
    if (provider.startsWith("google")) return "success";
    if (provider.startsWith("azure")) return "secondary";
    if (provider.startsWith("mistral")) return "warning";
    if (provider.startsWith("cohere")) return "error";
    return "default";
  };

  // Get provider display name
  const getProviderDisplayName = (provider: string): string => {
    // Extract the base provider and model name
    const parts = provider.split(":");
    if (parts.length === 1) {
      return provider.charAt(0).toUpperCase() + provider.slice(1);
    }

    const baseName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const model = parts.slice(1).join(":");

    return `${baseName} - ${model}`;
  };

  // Table columns
  const columns: TableColumn[] = [
    {
      field: "name",
      headerName: "Name",
      type: "link",
      linkPath: (row: Provider) => `/providers/${row._id}`,
    },
    {
      field: "provider",
      headerName: "Provider",
      type: "custom",
      render: (row: Provider) => (
        <Chip
          label={getProviderDisplayName(row.provider)}
          color={getProviderColor(row.provider)}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: "config",
      headerName: "Configuration",
      type: "custom",
      render: (row: Provider) => {
        return row.config && Object.keys(row.config).length > 0
          ? `${Object.keys(row.config).length} setting(s)`
          : "Default configuration";
      },
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      type: "custom",
      render: (row: Provider) => formatRelativeTime(new Date(row.updatedAt)),
    },
  ];

  // Handle edit
  const handleEdit = (provider: Provider) => {
    navigate(`/providers/${provider._id}`);
  };

  // Handle delete
  const handleDelete = (provider: Provider) => {
    setProviderToDelete(provider);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!providerToDelete) return;

    try {
      setIsDeleting(true);
      await api.providers.delete(providerToDelete._id);
      setProviders(providers.filter((p) => p._id !== providerToDelete._id));
      alert.success("Provider deleted successfully");
    } catch (error) {
      console.error("Error deleting provider:", error);
      alert.error("Failed to delete provider");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setProviderToDelete(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setProviderToDelete(null);
  };

  return (
    <Box>
      <PageHeader
        title="Providers"
        subtitle="Manage LLM providers for your evaluations"
        actionPath="/providers/new"
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Providers", path: "/providers" },
        ]}
      />

      <LoadingBackdrop open={loading && !error && providers.length === 0} />

      {error && (
        <AlertMessage
          open={Boolean(error)}
          onClose={() => setError(null)}
          severity="error"
          title="Error"
          message={error}
        />
      )}

      <DataTable
        title="All Providers"
        data={providers}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Provider"
        message={`Are you sure you want to delete the provider "${providerToDelete?.name}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonColor="error"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={isDeleting}
      />
    </Box>
  );
};

export default ProviderList;
